import { useState } from "react";
import { Modal, Typography, IconButton, Button, InputBase, Box, Skeleton } from "@mui/material";
import styles from "./stylesWithdraw.module.scss";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import SuccessfulSVG from "../../../../../assets/svg/SuccessfulSVG";
import Information from "../../../../../assets/svg/Information";
import CloseBtn from "@/assets/svg/CloseBtn";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import {
  useInitiateWithdrawalMutation,
  useResendWithdrawalOtpMutation,
  useConfirmWithdrawalMutation,
  useGetWithdrawalFeesQuery,
  useGetVerifiedAddressesQuery,
} from "@/store/api/walletApi";
// import LeftArrow from "@/assets/svg/LeftArrow";
import { useNavigate } from "react-router-dom";
// import cashImg from "@/assets/image/Cash.png";
import solanaImg from "@/assets/image/Solana.png";
// import lightningImg from "@/assets/image/BitcoinLightning.png";
import { formatMoney } from "@/helpers/formatMoney";

interface Props {
  onClose: () => void;
  maxAvailable?: number;
}

type Step = "select" | "form" | "submitting" | "verify" | "verifying" | "success";

interface Provider {
  id: string;
  apiName: string;
  name: string;
  description: string;
  addressLabel: string;
  placeholder: string;
  addressFormat: string;
  feeColor?: string;
  feePercent?: number;
  flatFee?: number;
  minAmount?: number;
  image: string;
  imageScale?: number;
  conversionLabel?: string;
  conversion: string;
  addressInfoLabel?: string;
  adress: string;
}

const PROVIDERS: Provider[] = [
  {
    id: "solana",
    apiName: "Solana",
    name: "Solana",
    description: "Send via Solana chain",
    addressLabel: "Solana wallet address",
    placeholder: "Enter your Solana wallet address",
    addressFormat: "Solana wallet address",
    flatFee: 1,
    image: solanaImg,
    imageScale: 1.58,
    conversionLabel: "Token:",
    conversion: "USDC (no conversion)",
    addressInfoLabel: "Wallet:",
    adress: "Solflare or Phantom",
  },
  // {
  //   id: "lightning",
  //   apiName: "Speed",
  //   name: "Bitcoin Lightning Network",
  //   description: "Via Lightning Network",
  //   addressLabel: "Lightning address",
  //   placeholder: "yourname@wallet.com",
  //   addressFormat: "yourname@wallet.com",
  //   feePercent: 2,
  //   minAmount: 11,
  //   image: lightningImg,
  //   imageScale: 1.34,
  //   conversion: "USDC → BTC → LN",
  //   adress: "yourname@wallet.com",
  // },
];

const WithdrawModal = ({ onClose, maxAvailable = 0 }: Props) => {
  const userEmail = useSelector((state: RootState) => state.auth.user?.email ?? "");
  const navigate = useNavigate();

  const { data: feesData } = useGetWithdrawalFeesQuery();
  const { data: verifiedAddresses } = useGetVerifiedAddressesQuery();

  const [initiateWithdrawal] = useInitiateWithdrawalMutation();
  const [resendWithdrawalOtp] = useResendWithdrawalOtpMutation();
  const [confirmWithdrawal] = useConfirmWithdrawalMutation();

  const getFeeInfo = (provider: Provider): { percentage?: number; flat?: number } => {
    const found = feesData?.find((f) => f.name === provider.apiName);
    if (found) {
      if (Number.isFinite(Number(found.percentage)))
        return { percentage: Number(found.percentage) };
      if (Number.isFinite(Number(found.flatFeeUsdt))) return { flat: Number(found.flatFeeUsdt) };
    }
    return {};
  };

  const getFeeLabel = (provider: Provider): string | null => {
    const info = getFeeInfo(provider);
    if (info.percentage != null) return `${info.percentage}%`;
    if (info.flat != null) return `$${formatMoney(info.flat)}`;
    return null;
  };

  const getFeeAmount = (provider: Provider, value: number): number | null => {
    const info = getFeeInfo(provider);
    if (info.percentage != null) return value * (info.percentage / 100);
    if (info.flat != null) return info.flat;
    return null;
  };

  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [step, setStep] = useState<Step>("select");
  const [code, setCode] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const isAmountValid =
    Number(amount) > 0 &&
    Number(amount) <= maxAvailable &&
    (!selectedProvider?.minAmount || Number(amount) >= selectedProvider.minAmount);
  const isAddressValid = address.trim().length > 3;
  const isFormValid = isAmountValid && isAddressValid;
  const isCodeValid = code.length === 6;
  const isSubmitting = step === "submitting" || step === "verifying";

  const handleSelectProvider = (provider: Provider) => {
    setSelectedProvider(provider);
    const savedAddress = verifiedAddresses?.find((item) => item.network === provider.id);
    setAddress(savedAddress?.address ?? "");
  };

  const handleNext = () => {
    if (selectedProvider) setStep("form");
  };

  // const handleBackToSelect = () => {
  //   setStep("select");
  //   setAddress("");
  //   setErrorMsg("");
  // };

  const handleAmountChange = (value: string) => {
    const sanitized = value.replace(/[^0-9.]/g, "");
    if (!sanitized) {
      setAmount("");
      return;
    }
    if (Number(sanitized) <= maxAvailable) setAmount(sanitized);
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;
    setErrorMsg("");
    setStep("submitting");
    try {
      const result = await initiateWithdrawal({
        amount: Number(amount),
        payoutAddress: address.trim(),
      }).unwrap();
      setSessionId(result.sessionId);
      setStep("verify");
    } catch (e: any) {
      setErrorMsg(e?.data?.message ?? "Failed to initiate withdrawal. Please try again.");
      setStep("form");
    }
  };

  const handleResend = async () => {
    if (!sessionId) return;
    setErrorMsg("");
    setCode("");
    try {
      await resendWithdrawalOtp({ sessionId }).unwrap();
    } catch (e: any) {
      setErrorMsg(e?.data?.message ?? "Failed to resend code. Please try again.");
    }
  };

  const handleVerify = async () => {
    if (!isCodeValid) return;
    setErrorMsg("");
    setStep("verifying");
    try {
      await confirmWithdrawal({ sessionId, otpCode: code }).unwrap();
      setStep("success");
    } catch (e: any) {
      setErrorMsg(e?.data?.message ?? "Invalid code. Please try again.");
      setStep("verify");
    }
  };

  return (
    <Modal open onClose={isSubmitting ? undefined : onClose}>
      <div className={styles.overlay} onClick={isSubmitting ? undefined : onClose}>
        <div className={styles.modalWrapper} onClick={(e) => e.stopPropagation()}>
          {isSubmitting && (
            <>
              <div className={styles.blurOverlay} />
              <div className={styles.loaderOverlay}>
                <CustomLoader loading isModal={false} size={42} />
              </div>
            </>
          )}

          <div
            className={`${styles.modal} ${step === "select" ? styles.modalSelect : ""} ${isSubmitting ? styles.modalBlurred : ""}`}
            style={
              step === "select"
                ? {
                    // height: "628px",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                  }
                : undefined
            }
          >
            <div
              className={styles.header}
              style={{ marginBottom: step === "select" ? "14px" : "24px" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {/* {step === "form" && (
                  <IconButton onClick={handleBackToSelect} sx={{ p: "4px" }}>
                    <LeftArrow />
                  </IconButton>
                )} */}
                <Typography className={styles.title}>Withdraw Funds</Typography>
              </Box>
              <IconButton onClick={onClose} className={styles.Btn} disabled={isSubmitting}>
                <CloseBtn />
              </IconButton>
            </div>

            {step === "select" && (
              <Box sx={{ mx: "-24px", borderBottom: "1px solid #F4F5F6", mb: "10px" }} />
            )}

            {/* STEP: select provider — scrollable list */}
            {step === "select" && (
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "6px",
                    width: "400px",
                    background: "#EFF6FF",
                    border: "1px solid #BEDBFF",
                    padding: "12px",
                    borderRadius: "10px",
                    mb: "6px",
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px", flexShrink: 0 }}>
                    <Information />
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "16px",
                        color: "rgba(20, 71, 230, 0.8)",
                      }}
                    >
                      Tip
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "12px",
                        lineHeight: "16px",
                        color: "rgba(20, 71, 230, 0.8)",
                      }}
                    >
                      Your balance is in USDC. Solana sends it straight through — 1% fee.
                    </Typography>
                  </Box>
                </Box>
                {PROVIDERS.map((provider) => {
                  const isSelected = selectedProvider?.id === provider.id;
                  return (
                    <Box
                      key={provider.id}
                      onClick={() => handleSelectProvider(provider)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        padding: "12px",
                        width: "400px",
                        boxSizing: "border-box",
                        flexShrink: 0,
                        border: isSelected ? "1px solid #2A85FF" : "1px solid #E4E7E9",
                        borderRadius: "12px",
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#F8FBFF" : "#fff",
                        "&:hover": { borderColor: "#2A85FF", backgroundColor: "#F8FBFF" },
                      }}
                    >
                      {/* Top row: icon + name/desc + fee */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Box
                          sx={{
                            width: "40px",
                            height: "40px",
                            flexShrink: 0,
                            borderRadius: "8px",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={provider.image}
                            alt={provider.name}
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                              transform: provider.imageScale
                                ? `scale(${provider.imageScale})`
                                : undefined,
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "16px",
                              lineHeight: "22px",
                              color: "#101828",
                            }}
                          >
                            {provider.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: "13px",
                              lineHeight: "16px",
                              color: "#7D8690",
                            }}
                          >
                            {provider.description}
                          </Typography>
                        </Box>
                        {feesData ? (
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "16px",
                              color: provider.feeColor ?? "#2A2D31",
                              flexShrink: 0,
                            }}
                          >
                            {getFeeLabel(provider) ?? "—"} fee
                          </Typography>
                        ) : (
                          <Skeleton
                            variant="rounded"
                            width={64}
                            height={20}
                            animation="wave"
                            sx={{ flexShrink: 0, borderRadius: "6px" }}
                          />
                        )}
                      </Box>

                      {/* Bottom: info box */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          background: "#FBFBFB",
                          border: "1px solid #F4F5F6",
                          padding: "10px 12px",
                          borderRadius: "10px",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: "4px" }}>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: "#383C40",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {provider.conversionLabel ?? "Conversion:"}
                          </Typography>
                          <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#52575D" }}>
                            {provider.conversion}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: "4px" }}>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: "#383C40",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {provider.addressInfoLabel ?? "Lightning address:"}
                          </Typography>
                          <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#155DFC" }}>
                            {provider.adress}
                          </Typography>
                        </Box>
                        {provider.minAmount ? (
                          <Box sx={{ display: "flex", gap: "4px" }}>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "#383C40",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Minimum:
                            </Typography>
                            <Typography
                              sx={{ fontSize: "12px", fontWeight: 500, color: "#52575D" }}
                            >
                              ${formatMoney(provider.minAmount)}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Next button — pinned outside scroll area */}
            {step === "select" && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!selectedProvider}
                className={`${styles.button} ${!selectedProvider ? styles.buttonDisabled : ""}`}
                sx={{ marginTop: "24px !important", flexShrink: 0 }}
              >
                Next
              </Button>
            )}

            {/* STEP: form */}
            {(step === "form" || step === "submitting") && selectedProvider && (
              <>
                <div className={styles.field}>
                  <Typography className={styles.label}>Amount (USDC)</Typography>
                  <div className={styles.amountWrapper}>
                    <span className={styles.currency}>$</span>
                    <InputBase
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      className={styles.amountInput}
                      inputMode="decimal"
                      type="number"
                      disabled={isSubmitting}
                    />
                  </div>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: "7px" }}>
                    {Number(amount) > 0 && selectedProvider ? (
                      !feesData ? (
                        <Skeleton
                          variant="rounded"
                          width={80}
                          height={16}
                          animation="wave"
                          sx={{ borderRadius: "6px" }}
                        />
                      ) : getFeeAmount(selectedProvider, Number(amount)) !== null ? (
                        <Typography className={styles.max} sx={{ mt: "0 !important" }}>
                          Fee:{" "}
                          <Box component="span" sx={{ fontWeight: 600, color: "#2A2D31" }}>
                            ${formatMoney(getFeeAmount(selectedProvider, Number(amount))!)}
                          </Box>
                        </Typography>
                      ) : (
                        <span />
                      )
                    ) : (
                      <span />
                    )}
                    <Typography className={styles.max} sx={{ mt: "0 !important" }}>
                      Max available: ${formatMoney(maxAvailable)}
                    </Typography>
                  </Box>
                  {selectedProvider.minAmount ? (
                    <Typography className={styles.max} sx={{ textAlign: "right !important" }}>
                      Min withdrawal: ${formatMoney(selectedProvider.minAmount)}
                    </Typography>
                  ) : null}
                </div>

                <div className={styles.fieldAddress}>
                  <Typography className={styles.label}>{selectedProvider.addressLabel}</Typography>
                  <InputBase
                    placeholder={selectedProvider.placeholder}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className={styles.input}
                    maxRows={1}
                    disabled={isSubmitting}
                  />
                  {/* <Box
                    sx={{
                      mt: "8px",
                      p: "10px 12px",
                      borderRadius: "8px",
                      backgroundColor: "#F8FBFF",
                      border: "1px solid #DDEEFF",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "12px", color: "#6A7282" }}>
                      Format:{" "}
                      <Box component="span" sx={{ color: "#2A85FF", fontWeight: 500 }}>
                        {selectedProvider.addressFormat}
                      </Box>
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#6A7282" }}>
                      Fee:{" "}
                      <Box component="span" sx={{ fontWeight: 700, color: "#101828" }}>
                        {selectedProvider.fee}
                        {Number(amount) > 0 &&
                          (() => {
                            const feePercent = parseFloat(selectedProvider.fee) / 100;
                            const feeAmount = (Number(amount) * feePercent).toFixed(2);
                            return ` · $${feeAmount}`;
                          })()}
                      </Box>
                    </Typography>
                  </Box> */}
                </div>

                <Typography
                  className={`${styles.errorMsg} ${!errorMsg ? styles.errorMsgHidden : ""}`}
                >
                  {errorMsg || " "}
                </Typography>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!isFormValid || isSubmitting}
                  className={`${styles.button} ${!isFormValid ? styles.buttonDisabled : ""}`}
                >
                  Request Withdrawal
                </Button>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    mt: "16px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 500, lineHeight: "22px", color: "#9DA6B0" }}
                  >
                    Have questions? Go to the
                  </Typography>
                  <Typography
                    onClick={() => {
                      onClose();
                      navigate("/faq");
                    }}
                    sx={{
                      color: "#2A85FF",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "22px",
                      cursor: "pointer",
                      // "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    FAQ section
                  </Typography>
                </Box>
              </>
            )}

            {(step === "verify" || step === "verifying") && (
              <>
                <Typography className={styles.verifyTitle}>Two-Factor Authentication</Typography>
                <Typography className={styles.verifyDescription}>
                  We've sent a 6-digit code to your email <br />
                  <span className={styles.verifyEmail}>{userEmail}</span>
                  <br />
                  Please enter it below to confirm your withdrawal.
                </Typography>

                <div className={styles.fieldVerify}>
                  <InputBase
                    placeholder="Enter 6-digit code"
                    value={code}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setCode(val);
                    }}
                    className={styles.codeInput}
                    disabled={isSubmitting}
                    inputProps={{
                      maxLength: 6,
                      style: {
                        fontSize: "14px",
                        lineHeight: "157%",
                        fontWeight: "500",
                        color: "#2A2D31",
                      },
                    }}
                  />
                </div>

                <Typography
                  className={`${styles.errorMsg} ${!errorMsg ? styles.errorMsgHidden : ""}`}
                >
                  {errorMsg || " "}
                </Typography>

                <Typography className={styles.resendRow}>
                  Didn't receive a code?{" "}
                  <span className={styles.resendLink} onClick={handleResend}>
                    Send code again
                  </span>
                </Typography>

                <Button
                  variant="contained"
                  onClick={handleVerify}
                  disabled={!isCodeValid || isSubmitting}
                  className={`${styles.buttonVerify} ${!isCodeValid ? styles.buttonDisabled : ""}`}
                >
                  Verify & Withdraw
                </Button>
              </>
            )}

            {step === "success" && (
              <div className={styles.successContent}>
                <div className={styles.successIcon}>
                  <SuccessfulSVG />
                </div>
                <Typography className={styles.successTitle}>Withdrawal Completed</Typography>
                <Typography className={styles.successDescription}>
                  Your withdrawal has been completed. <br /> The funds have been sent and should
                  arrive <br /> at your address instantly.
                </Typography>
                <Button variant="contained" onClick={onClose} className={styles.buttonSuccess}>
                  Done
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default WithdrawModal;
